import React, { useId, useState, ChangeEventHandler } from 'react';
import Image from 'next/legacy/image';
import cx from 'classnames';

import { BaseInputProps, TextInputWidth } from '../shared';

import icon from '@/images/error_icon.svg';
import arrowDown from '@/images/arrow-down.svg';

export interface SelectOptions {
  key: string | number;
  value: string;
}

interface TextInputProps extends BaseInputProps {
  options: SelectOptions[];
  handleOnChange: ChangeEventHandler<HTMLSelectElement>;
  variant?: 'default' | 'filterbar' | 'gl4c' | 'selectstate';
  className?: string;
  wrapperClassName?: string;
  labelClassName?: string;
}

export function Select({
  options,
  value,
  type,
  label,
  error,
  required,
  handleOnChange,
  disabled = false,
  width = TextInputWidth.FULL,
  variant = 'default',
  className = '',
  wrapperClassName = '',
  labelClassName = ''
}: TextInputProps) {
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const inputId = useId();

  return (
    <>
      <div
        className={cx(
          'relative rounded-input-box-shape border p-0 transition-all duration-300',
          error ? 'mb-2 border-hibiscus' : 'mb-4 border-teal',
          variant === 'filterbar' && 'mb-0 h-8 rounded-full',
          variant !== 'filterbar' && 'h-16',
          wrapperClassName
        )}
        style={{ width: `${width}%` }}
      >
        <label
          data-testid={`${label}-testid`}
          htmlFor={inputId}
          className={cx(
            'absolute rounded-input-box-shape font-medium transition-all duration-300',
            variant === 'filterbar' &&
              'px-4 py-1.5 text-base font-normal text-text',
            ['filterbar', 'selectstate'].includes(variant) &&
              (!!value || isFocused) &&
              'invisible m-0',
            variant !== 'filterbar' &&
              variant !== 'selectstate' &&
              !value &&
              !isFocused &&
              'pointer-events-none top-1/2 -translate-y-1/2 pl-4 text-[1em]',
            variant !== 'filterbar' &&
              variant !== 'selectstate' &&
              (!!value || isFocused) &&
              'pointer-events-none top-0 -mt-2 ml-4 translate-y-0 bg-white px-1 text-[0.8em]',
            labelClassName
          )}
        >
          {label}
          {required ? '*' : ''}
        </label>

        <select
          id={inputId}
          value={value}
          data-testid={`select-${type}-${label}`}
          onChange={handleOnChange}
          required={required}
          disabled={disabled}
          onFocus={() => {
            setIsFocused(true);
          }}
          onBlur={() => {
            setIsFocused(false);
          }}
          className={cx(
            'select-input',
            'h-full w-full rounded-input-box-shape border-none',
            'px-4 leading-[1.5] text-black',
            'bg-white bg-no-repeat focus:outline-none',
            variant === 'filterbar' &&
              'box-border h-[30px] rounded-full px-4 py-0',
            className
          )}
          style={{
            backgroundImage: `url(${arrowDown.src})`,
            backgroundPosition: 'calc(100% - 22px) center',
            backgroundSize: '12px'
          }}
        >
          {options.map((option) => (
            <option key={option.key}>{option.value}</option>
          ))}
        </select>
      </div>
      <div
        data-testid={`input-${type}-${label}-error`}
        className={cx(
          'select-error-message flex items-center overflow-hidden text-hibiscus',
          error ? 'mb-4 max-h-[24px] opacity-100' : 'mb-0 max-h-0 opacity-0',
          variant === 'filterbar' && 'hidden'
        )}
      >
        <div className="ml-4 mr-2 flex h-full">
          <Image src={icon} alt="Form input error" />
        </div>
        {error || ''}
      </div>
    </>
  );
}
